// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Shubham Dixit <shubham@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

export function LinkExpiredScreen() {
  const navigate = useNavigate()
  const { t } = useTranslation()

  return (
    <div className="min-h-dvh flex flex-col bg-gradient-to-br from-[#1C0A00] via-[#2B1200] to-[#1C1C1E] relative overflow-hidden">
      {/* Amber/red glow blobs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-24 right-[-15%] w-[420px] h-[420px] rounded-full bg-amber-500/15 blur-[130px] animate-pulse" />
        <div className="absolute bottom-[15%] -left-16 w-[300px] h-[300px] rounded-full bg-red-600/10 blur-[100px] animate-pulse [animation-delay:1.5s]" />
        <div className="absolute top-[40%] right-[10%] w-[200px] h-[200px] rounded-full bg-orange-400/8 blur-[80px] animate-pulse [animation-delay:0.8s]" />
      </div>

      {/* Top bar: QH mark + close */}
      <div className="relative z-10 flex items-center justify-between px-6 pt-12 sm:px-8">
        <div className="h-8 w-8 rounded-full bg-white/90 flex items-center justify-center shadow-[0_0_20px_rgba(255,255,255,0.15)]">
          <span className="text-[#1C1C1E] text-[10px] font-black leading-none tracking-tight">QH</span>
        </div>
        <button
          onClick={() => navigate('/')}
          aria-label={t('common.close')}
          className="h-8 w-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
        >
          <span className="text-white/50 text-base leading-none select-none">✕</span>
        </button>
      </div>

      {/* Main content */}
      <div className="relative z-10 flex flex-col flex-1 items-center justify-center px-6 pb-16 sm:px-8">
        <div className="w-full max-w-xs flex flex-col items-center text-center">

          {/* "!" icon with pulsing red glow */}
          <div className="relative mb-8">
            <div className="absolute inset-0 rounded-full bg-red-500/30 blur-2xl scale-[1.8] animate-pulse" />
            <div className="relative h-[120px] w-[120px] rounded-full border-2 border-red-400/70 bg-red-500/15 flex items-center justify-center shadow-[0_0_60px_rgba(239,68,68,0.4),inset_0_0_30px_rgba(239,68,68,0.1)]">
              <span className="text-red-400 text-[60px] font-black leading-none select-none">!</span>
            </div>
          </div>

          {/* Heading */}
          <h1 className="text-[26px] font-bold text-white leading-tight mb-4">
            {t('linkExpired.title')}
          </h1>

          {/* Body — two lines */}
          <p className="text-white/60 text-[15px] leading-relaxed mb-12">
            {t('linkExpired.body1')}
            <br />
            {t('linkExpired.body2')}
          </p>

          {/* Back to home button */}
          <button
            onClick={() => navigate('/')}
            className="w-full h-14 rounded-2xl font-bold text-base text-white bg-gradient-to-r from-[#006EF9] to-[#02C7C3] shadow-[0_0_40px_rgba(0,110,249,0.4)] hover:shadow-[0_0_60px_rgba(2,199,195,0.5)] hover:scale-[1.02] active:scale-95 transition-all duration-200"
          >
            {t('linkExpired.backToHome')}
          </button>
        </div>
      </div>
    </div>
  )
}
